/**
 * Dashboard View — admin at-a-glance overview.
 *
 * Reading order (top → bottom): Pulse (hero KPIs) → Funnel score + actions →
 * Funnel-at-a-glance snapshot → acquisition context → systems-health strip →
 * doorways. Every funnel number comes from the canonical `snapshot`
 * (GET /admin/analytics/funnel/dashboard-summary), which shares its SQL with the
 * Funnel Trends tab — so the two never disagree. Traffic/API KPIs come from
 * `data` (report/preview). This is a SNAPSHOT (current vs previous period); the
 * over-time charts live in the Funnel Trends tab and are deliberately not
 * duplicated here.
 */
import { COLORS } from './shared/chartTheme.js';
import { TimeSeriesChart } from './shared/ChartComponents.jsx';
import { formatNumber, formatEndpointLabel } from './shared/formatters.js';
import { Icons } from './shared/Icons.jsx';
import { InfoTooltip } from './shared/InfoTooltip.jsx';
import { KPICard } from './shared/KPICard.jsx';

// External dashboard URLs
const PLAUSIBLE_URL = 'https://analytics.batemo.com/insights.batemo.com';

// Conversion-rate targets (%) for the canonical funnel stages. Weights bias the
// health score toward revenue-proximate stages.
const CONVERSIONS_META = {
  demo_requested__demo_accepted: { target: 70, weight: 20 },
  demo_accepted__active_demo: { target: 60, weight: 30 },
  active_demo__pro: { target: 15, weight: 50 },
};

// Delta-gating (mirrors funnel_briefing.py): a %-delta is only meaningful when
// the previous-period base is large enough, and the active-demo activity signal
// is only comparable from this date on (heartbeat tracking start).
const MIN_DENOM = 10;
const ACTIVITY_BASELINE = '2026-05-18';
// Stages whose value depends on the post-activation activity signal.
const ACTIVITY_STAGES = new Set(['active_demo', 'pro']);

function getHealthColor(score) {
  if (score >= 70) return COLORS.green;
  if (score >= 40) return COLORS.orange;
  return COLORS.red;
}

/**
 * Build the absolute + gated-percentage delta for a stage. Returns a compact
 * display string + direction, or null when there is no previous value.
 */
function stageDelta(stage, activityReliable) {
  if (!stage || stage.previous_value == null) return null;
  const diff = (stage.value ?? 0) - stage.previous_value;
  const dir = diff > 0 ? 'up' : diff < 0 ? 'down' : 'flat';
  const sign = diff > 0 ? '+' : diff < 0 ? '−' : '';
  const arrow = diff > 0 ? '↑' : diff < 0 ? '↓' : '→';
  const reliable = !ACTIVITY_STAGES.has(stage.stage_id) || activityReliable;
  let text;
  if (reliable && stage.previous_value >= MIN_DENOM && stage.change_percent != null) {
    text = `${sign}${Math.abs(stage.change_percent).toFixed(1)}%`;
  } else {
    // Small base or pre-baseline → absolute change is the honest signal.
    text = `${sign}${Math.abs(diff)}`;
  }
  return { text: `${arrow} ${text} vs prev`, dir };
}

/**
 * Derive the dashboard model (health score, recommendations) from the canonical
 * snapshot. Rule-based — no LLM, no causal claims.
 */
function buildModel(snapshot) {
  const stages = snapshot.stages || [];
  const conversions = snapshot.conversions || [];
  const labelById = Object.fromEntries(stages.map((s) => [s.stage_id, s.label]));
  const convByKey = Object.fromEntries(
    conversions.map((c) => [`${c.from_stage}__${c.to_stage}`, c])
  );

  // Weighted health score, each conversion capped at 1.0× its target.
  let score = 0;
  let totalWeight = 0;
  Object.entries(CONVERSIONS_META).forEach(([key, { target, weight }]) => {
    const c = convByKey[key];
    if (c) {
      score += weight * Math.min((c.rate || 0) / target, 1.0);
      totalWeight += weight;
    }
  });
  score = totalWeight > 0 ? Math.min(Math.round(score / totalWeight * 100), 100) : 0;

  // Rule-based recommendations → "which measures make sense to optimise".
  const recs = [];
  const stock = snapshot.demo_lifecycle?.active_demo_stock || 0;
  const proConv = convByKey['active_demo__pro'];
  if (proConv && (proConv.rate || 0) === 0 && stock > 0) {
    recs.push({
      severity: 'critical',
      title: 'No PRO conversions',
      recommendation: `${stock} live demo${stock === 1 ? '' : 's'} but no PRO conversion this period — reach out to demo users before their access expires.`,
      view: 'funnel-trends',
    });
  }
  Object.entries(CONVERSIONS_META).forEach(([key, { target }]) => {
    const c = convByKey[key];
    if (!c) return;
    const ratio = target > 0 ? (c.rate || 0) / target : 1;
    const name = `${labelById[c.from_stage] || c.from_stage} → ${labelById[c.to_stage] || c.to_stage}`;
    if (proConv && key === 'active_demo__pro' && (c.rate || 0) === 0 && stock > 0) return; // covered above
    if (ratio < 0.5) {
      recs.push({
        severity: 'critical',
        title: `${name} is the biggest drop-off`,
        recommendation: `Converting at ${(c.rate || 0).toFixed(1)}% vs a ${target}% target (${c.numerator}/${c.denominator}).`,
        view: 'funnel-trends',
      });
    } else if (ratio < 1) {
      recs.push({
        severity: 'warning',
        title: `${name} below target`,
        recommendation: `${(c.rate || 0).toFixed(1)}% vs ${target}% target — review this step.`,
        view: 'funnel-trends',
      });
    }
  });

  return {
    health_score: score,
    health_label: score >= 70 ? 'Healthy' : score >= 40 ? 'Needs Attention' : 'Critical',
    recommendations: recs,
    labelById,
    convByKey,
  };
}

/** Add API-health recommendations derived from the preview metrics. */
function apiRecommendations(data) {
  const recs = [];
  const m = data?.api_metrics || {};
  if (m.error_rate > 5) {
    recs.push({
      severity: 'warning',
      title: 'API error rate elevated',
      recommendation: `${m.error_rate.toFixed(2)}% of API requests are failing — may be hurting demo engagement.`,
      view: 'api',
    });
  }
  if (m.avg_response_time_ms > 500) {
    recs.push({
      severity: 'warning',
      title: 'API responses are slow',
      recommendation: `Average response time is ${Math.round(m.avg_response_time_ms)}ms (>500ms).`,
      view: 'api',
    });
  }
  return recs;
}

// ============= SUB-COMPONENTS =============

function FunnelHealthScore({ score, label }) {
  const color = getHealthColor(score);
  return (
    <div className="analytics-funnel-health">
      <div
        className="funnel-health-score"
        style={{ borderColor: color }}
        title="Funnel Score — weighted score (0-100) of conversion rates vs targets"
      >
        <span className="funnel-health-number" style={{ color }}>{score}</span>
        <span className="funnel-health-label">Funnel Score</span>
      </div>
      <div className="funnel-health-alerts">
        <div className="funnel-health-alert" style={{ borderLeftColor: color }}>
          <span className="alert-title">{label}</span>
          <span className="alert-recommendation">
            Weighted across Demo Requested → Accepted → Active → PRO.
          </span>
        </div>
      </div>
    </div>
  );
}

function RecommendationsPanel({ recommendations, onNavigate }) {
  const recs = recommendations.length
    ? recommendations
    : [{
        severity: 'success',
        title: 'All tracked conversions at or above target',
        recommendation: 'No funnel bottleneck detected for the selected period.',
      }];
  return (
    <div className="dashboard-recommendations">
      <h4 className="dashboard-widget-title">Recommended Actions</h4>
      <div className="funnel-health-alerts">
        {recs.slice(0, 3).map((rec, idx) => (
          <div
            key={idx}
            className={`funnel-health-alert funnel-health-alert--${rec.severity}`}
            onClick={rec.view ? () => onNavigate(rec.view) : undefined}
            style={rec.view ? { cursor: 'pointer' } : undefined}
            title={rec.view ? 'Open the relevant tab' : undefined}
          >
            <span className="alert-title">{rec.title}</span>
            <span className="alert-recommendation">{rec.recommendation}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Stage tiles (value + label + gated delta) — a snapshot, no time axis. */
function FunnelStageStrip({ stages, activityReliable }) {
  return (
    <div className="analytics-trend-summary">
      {stages.map((stage) => {
        const delta = stageDelta(stage, activityReliable);
        const color = delta?.dir === 'up' ? COLORS.green
          : delta?.dir === 'down' ? COLORS.red : COLORS.gray;
        return (
          <div className="trend-item" key={stage.stage_id}>
            <span className="trend-item-value">{formatNumber(stage.value ?? 0)}</span>
            <span className="trend-item-label">{stage.label}</span>
            {delta && (
              <span className="trend-item-change" style={{ color }}>{delta.text}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Conversion rates vs target — bar meters, color = target achievement. */
function FunnelConversionRates({ conversions, labelById }) {
  return (
    <div className="dashboard-funnel-mini">
      <h4 className="dashboard-widget-title">
        Conversion Rates <InfoTooltip infoKey="funnelConversionRate" />
      </h4>
      <div className="dashboard-funnel-mini-stages">
        {conversions.map((c) => {
          const meta = CONVERSIONS_META[`${c.from_stage}__${c.to_stage}`] || { target: 100 };
          const target = meta.target;
          const ratio = target > 0 ? (c.rate || 0) / target : 0;
          const barColor = ratio >= 1 ? COLORS.green : ratio >= 0.6 ? COLORS.orange : COLORS.red;
          const trend = (c.rate || 0) - (c.previous_rate || 0);
          const label = `${labelById[c.from_stage] || c.from_stage} → ${labelById[c.to_stage] || c.to_stage}`;
          return (
            <div key={`${c.from_stage}__${c.to_stage}`} className="dashboard-funnel-mini-stage">
              <div className="dashboard-funnel-mini-header">
                <span className="dashboard-funnel-mini-label">{label}</span>
                <span className="dashboard-funnel-mini-rate" style={{ color: barColor }}>
                  {(c.rate || 0).toFixed(1)}%
                </span>
              </div>
              <div className="dashboard-funnel-mini-bar-track">
                <div
                  className="dashboard-funnel-mini-bar-fill"
                  style={{ width: `${Math.min(ratio * 100, 100)}%`, backgroundColor: barColor }}
                />
                <div className="dashboard-funnel-mini-bar-target" style={{ left: '100%' }}
                     title={`Target: ${target}%`} />
              </div>
              <div className="dashboard-funnel-mini-footer">
                <span className="dashboard-funnel-mini-target">
                  {c.numerator}/{c.denominator} · Target {target}%
                </span>
                <span className={`dashboard-funnel-mini-trend ${
                  trend > 0 ? 'dashboard-funnel-mini-trend--up'
                    : trend < 0 ? 'dashboard-funnel-mini-trend--down' : ''}`}>
                  {trend > 0 ? '↑' : trend < 0 ? '↓' : '↔'}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Demo lifecycle — live stock split organic vs trade fair (current state). */
function DemoStock({ lifecycle }) {
  return (
    <div className="funnel-demo-lifecycle">
      <h4 className="dashboard-widget-title">
        Active Demo Stock <InfoTooltip infoKey="funnelActiveDemos" />
      </h4>
      <div className="funnel-lifecycle-grid">
        <div className="funnel-lifecycle-card funnel-lifecycle-card--active">
          <div className="funnel-lifecycle-value">{formatNumber(lifecycle.active_demo_stock || 0)}</div>
          <div className="funnel-lifecycle-label">Total live demos</div>
          <div className="funnel-lifecycle-icon" style={{ color: COLORS.green }}>{Icons.zap}</div>
        </div>
        <div className="funnel-lifecycle-card funnel-lifecycle-card--post">
          <div className="funnel-lifecycle-value">{formatNumber(lifecycle.active_demo_organic || 0)}</div>
          <div className="funnel-lifecycle-label">Organic</div>
          <div className="funnel-lifecycle-icon" style={{ color: COLORS.cyan }}>{Icons.users}</div>
        </div>
        <div className="funnel-lifecycle-card">
          <div className="funnel-lifecycle-value">{formatNumber(lifecycle.active_demo_tradefair || 0)}</div>
          <div className="funnel-lifecycle-label">Trade fair</div>
          <div className="funnel-lifecycle-icon" style={{ color: COLORS.orange }}>{Icons.funnel}</div>
        </div>
      </div>
    </div>
  );
}

/** Compact systems-health strip — one signal per system + deep-link. */
function SystemsHealthStrip({ data, onNavigate }) {
  const pl = data?.plausible_data || {};
  const api = data?.api_metrics || {};
  const errColor = api.error_rate > 5 ? COLORS.red : api.error_rate > 1 ? COLORS.orange : COLORS.green;
  const rtColor = api.avg_response_time_ms > 500 ? COLORS.red
    : api.avg_response_time_ms > 200 ? COLORS.orange : COLORS.green;
  const pills = [
    {
      label: 'Traffic', icon: Icons.users, color: COLORS.cyan, view: 'traffic',
      desc: `${formatNumber(pl.visitors || 0)} visitors · ${formatNumber(pl.pageviews || 0)} views`,
    },
    {
      label: 'API Performance', icon: Icons.zap, color: rtColor, view: 'api',
      desc: `${Math.round(api.avg_response_time_ms || 0)}ms · ${(api.error_rate || 0).toFixed(2)}% errors`,
    },
    {
      label: 'Cell Access', icon: Icons.chart, color: COLORS.purple, view: 'cell-access',
      desc: 'Access patterns & limits',
    },
  ];
  return (
    <div className="dashboard-quick-actions">
      <h4 className="dashboard-widget-title">Systems Health</h4>
      <div className="dashboard-actions-grid">
        {pills.map((p, idx) => (
          <button key={idx} className="dashboard-action-card" onClick={() => onNavigate(p.view)}>
            <span className="dashboard-action-icon" style={{ color: p.color }}>{p.icon}</span>
            <div>
              <span className="dashboard-action-label">{p.label}</span>
              <span className="dashboard-action-desc">{p.desc}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

/** Median time-to-convert bars (canonical, from subscription history). */
function DashboardTimeToConvert({ ttc }) {
  const stages = [
    { label: 'Registration → Active Demo', value: ttc.registration_to_active, color: COLORS.cyan },
    { label: 'Active Demo → PRO', value: ttc.active_to_pro, color: COLORS.green },
  ];
  const maxDays = Math.max(...stages.map((s) => s.value || 0), 1);
  return (
    <div className="funnel-time-to-convert">
      <h4 className="dashboard-widget-title">Median Time to Convert (Days)</h4>
      <div className="funnel-ttc-bars">
        {stages.map((stage, idx) => (
          <div key={idx} className="funnel-ttc-row">
            <span className="funnel-ttc-label">{stage.label}</span>
            <div className="funnel-ttc-bar-track">
              {stage.value != null ? (
                <div className="funnel-ttc-bar-fill"
                     style={{ width: `${(stage.value / maxDays) * 100}%`, backgroundColor: stage.color }} />
              ) : (
                <div className="funnel-ttc-bar-empty">n/a</div>
              )}
            </div>
            <span className="funnel-ttc-value">{stage.value != null ? `${stage.value} d` : '--'}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Compact LP-visitors-by-source breakdown (honest landing-by-source only). */
function DashboardSources({ sources }) {
  if (!sources || sources.length === 0) {
    return (
      <div className="funnel-source-comparison">
        <h4 className="dashboard-widget-title">LP Visitors by Source</h4>
        <div className="metric-row metric-row--empty"><span className="metric-label">No data</span></div>
      </div>
    );
  }
  const max = Math.max(...sources.map((s) => s.visitors), 1);
  const palette = [COLORS.cyan, COLORS.orange, COLORS.purple, COLORS.green, COLORS.gray, COLORS.red];
  return (
    <div className="funnel-source-comparison">
      <h4 className="dashboard-widget-title">LP Visitors by Source</h4>
      <div className="funnel-stage-sources">
        {sources.map((s, i) => (
          <div key={s.source} className="funnel-source-row">
            <span className="funnel-source-name">{s.source}</span>
            <div className="funnel-source-bar-track">
              <div className="funnel-source-bar-fill"
                   style={{ width: `${(s.visitors / max) * 100}%`, backgroundColor: palette[i % palette.length] }} />
            </div>
            <span className="funnel-source-value">{formatNumber(s.visitors)} ({s.share.toFixed(0)}%)</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function DashboardQuickActions({ onNavigate }) {
  const actions = [
    { label: 'Funnel Trends', desc: 'Conversion over time', icon: Icons.funnel, color: COLORS.orange, view: 'funnel-trends' },
    { label: 'Traffic', desc: 'Visitors & page views', icon: Icons.users, color: COLORS.cyan, view: 'traffic' },
    { label: 'API Performance', desc: 'Requests & response times', icon: Icons.trendUp, color: COLORS.green, view: 'api' },
    { label: 'AI Insights', desc: 'Questions about your data', icon: Icons.bot, color: COLORS.purple, view: 'ai-chat' },
  ];
  return (
    <div className="dashboard-quick-actions">
      <h4 className="dashboard-widget-title">Quick Actions</h4>
      <div className="dashboard-actions-grid">
        {actions.map((action, idx) => (
          <button key={idx} className="dashboard-action-card" onClick={() => onNavigate(action.view)}>
            <span className="dashboard-action-icon" style={{ color: action.color }}>{action.icon}</span>
            <div>
              <span className="dashboard-action-label">{action.label}</span>
              <span className="dashboard-action-desc">{action.desc}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ============= MAIN COMPONENT =============

export function AnalyticsDashboardView({ data, snapshot, periodDays, onNavigate }) {
  const stages = snapshot?.stages || [];
  const conversions = snapshot?.conversions || [];
  const lifecycle = snapshot?.demo_lifecycle || {};
  const trafficRef = snapshot?.traffic_ref || {};
  const timeToConvert = snapshot?.time_to_convert || {};
  const sources = snapshot?.source_attribution || [];
  const activityReliable = (snapshot?.period?.start || '9999') >= ACTIVITY_BASELINE;

  const model = snapshot ? buildModel(snapshot) : null;
  const recommendations = model
    ? [...model.recommendations, ...apiRecommendations(data)]
    : apiRecommendations(data);

  const stageById = Object.fromEntries(stages.map((s) => [s.stage_id, s]));
  const activeDemo = stageById['active_demo'];
  const pro = stageById['pro'];
  const adDelta = stageDelta(activeDemo, activityReliable);
  const proDelta = stageDelta(pro, activityReliable);

  return (
    <div className="analytics-overview">
      {/* A — Hero KPIs */}
      <div className="analytics-kpi-grid">
        <KPICard
          icon={Icons.users}
          label="Insights LP Visitors"
          value={formatNumber(trafficRef.lp_visitors ?? data.plausible_data?.visitors)}
          sublabel="Landing-page reach"
          color="cyan"
        />
        <KPICard
          icon={Icons.funnel}
          label="Registrations"
          value={formatNumber(trafficRef.registrations ?? 0)}
          sublabel="Email-verified signups"
          color="orange"
        />
        <KPICard
          icon={Icons.zap}
          label="New Active Demos"
          value={formatNumber(activeDemo?.value ?? 0)}
          sublabel={adDelta ? adDelta.text : 'This period'}
          color="green"
        />
        <KPICard
          icon={Icons.trendUp}
          label="PRO Conversions"
          value={formatNumber(pro?.value ?? 0)}
          sublabel={proDelta ? proDelta.text : 'This period'}
          color="purple"
        />
        <KPICard
          icon={Icons.zap}
          label="Active Demo Stock"
          value={formatNumber(lifecycle.active_demo_stock ?? 0)}
          sublabel={`${formatNumber(lifecycle.active_demo_organic ?? 0)} organic · ${formatNumber(lifecycle.active_demo_tradefair ?? 0)} trade fair`}
          color="cyan"
        />
        <KPICard
          icon={Icons.users}
          label="Website Visitors"
          value={formatNumber(data.plausible_data?.visitors)}
          sublabel="Whole site (Plausible)"
          color="orange"
        />
      </div>

      {snapshot ? (
        <>
          {/* B — Pulse: Funnel score + Recommendations */}
          <div className="analytics-dashboard-funnel-overview">
            <div className="analytics-dashboard-funnel-card">
              <h3 className="dashboard-widget-title">Funnel Score</h3>
              <FunnelHealthScore score={model.health_score} label={model.health_label} />
            </div>
            <div className="analytics-dashboard-funnel-card">
              <RecommendationsPanel recommendations={recommendations} onNavigate={onNavigate} />
            </div>
          </div>

          {/* C — Funnel-at-a-glance: snapshot stages + conversion rates */}
          <div className="analytics-dashboard-funnel-card">
            <h3 className="dashboard-widget-title">
              Funnel Snapshot — last {periodDays} days
              {!activityReliable && (
                <span className="funnel-filter-badge" title="Active-demo signal (heartbeat) starts 2026-05-18; deltas before then show absolute change">
                  pre-2026-05-18: Δ shown absolute
                </span>
              )}
            </h3>
            <FunnelStageStrip stages={stages} activityReliable={activityReliable} />
            <FunnelConversionRates conversions={conversions} labelById={model.labelById} />
          </div>

          {/* D — Acquisition context: traffic trend + demo stock */}
          <div className="dashboard-mid-row">
            <TimeSeriesChart
              title="Traffic Trend"
              data={data.plausible_data?.timeseries || []}
              xKey="date"
              series={[
                { name: 'Visitors', key: 'visitors', color: COLORS.cyan, area: true },
                { name: 'Page views', key: 'pageviews', color: COLORS.orange },
              ]}
              height={260}
              infoKey="trafficTrend"
            />
            <DemoStock lifecycle={lifecycle} />
          </div>

          {/* D2 — Diagnostics: time-to-convert + source breakdown */}
          <div className="dashboard-mid-row">
            <DashboardTimeToConvert ttc={timeToConvert} />
            <DashboardSources sources={sources} />
          </div>
        </>
      ) : (
        <div className="analytics-error" style={{ margin: '16px 0' }}>
          <span>{Icons.alertTriangle}</span>
          <p>Funnel snapshot unavailable right now. Traffic & API KPIs above are still live.</p>
        </div>
      )}

      {/* E — Systems health strip */}
      <SystemsHealthStrip data={data} onNavigate={onNavigate} />

      {/* F — Quick actions + external links */}
      <DashboardQuickActions onNavigate={onNavigate} />

      <div className="analytics-external-links">
        <a href={PLAUSIBLE_URL} target="_blank" rel="noopener noreferrer" className="external-link-card">
          <span className="external-link-icon">{Icons.users}</span>
          <div>
            <span className="external-link-title">Plausible Analytics</span>
            <span className="external-link-desc">Website traffic & goal tracking</span>
          </div>
          <span className="external-link-arrow">{Icons.externalLink}</span>
        </a>
      </div>
    </div>
  );
}
